import React from 'react';
import { FaRocket, FaHeart, FaLightbulb, FaUsers, FaGlobe, FaCode, FaPlane, FaArrowRight, FaStar, FaMagic } from 'react-icons/fa';
import SEO from './SEO';
import InternalLinks from './InternalLinks';
import BASE_URL, { SEO_BASE_URL } from '../config';

const AboutUs = ({ onBack, onNavigate }) => {
  const projects = [
    {
      name: 'ItinerarySeek',
      url: BASE_URL,
      icon: FaPlane,
      color: 'from-primary-500 to-secondary-500',
      shadowColor: 'shadow-[0_10px_40px_rgba(255,107,107,0.3)]',
      description: 'AI-powered travel planning that transforms your dream vacations into perfectly crafted itineraries. Smart, personalized, and effortless trip planning.',
      features: ['AI-Generated Itineraries', 'Group Trip Planning', 'Real-time Collaboration', 'Smart Recommendations']
    },
  ];

  const values = [
    {
      icon: FaLightbulb,
      title: 'Innovation First',
      description: 'We leverage cutting-edge AI and technology to solve real-world problems in creative ways.'
    },
    {
      icon: FaHeart,
      title: 'User-Centric Design',
      description: 'Every feature we build starts with understanding what our users truly need.'
    },
    {
      icon: FaUsers,
      title: 'Community Driven',
      description: 'We believe in building products that bring people together and foster collaboration.'
    },
    {
      icon: FaStar,
      title: 'Excellence Always',
      description: 'We\'re committed to delivering exceptional quality in everything we create.'
    }
  ];

  return (
    <>
      <SEO
        title="About Us"
        description="Learn about ItinerarySeek - the AI-powered travel planning platform that helps you create personalized itineraries in seconds. Discover our mission to make travel planning effortless."
        canonical={`${SEO_BASE_URL}/about`}
      />
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-primary-50">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary-200 to-secondary-200 rounded-full opacity-30 blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-secondary-200 to-primary-200 rounded-full opacity-30 blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative">
          {/* Back Button */}
          <button
            onClick={onBack}
            className="mb-8 flex items-center gap-2 text-neutral-600 hover:text-primary-500 transition-colors group"
          >
            <span className="transform group-hover:-translate-x-1 transition-transform">&larr;</span>
            Back to Dashboard
          </button>

          {/* Main Hero */}
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-4 mb-6">
              <span className="h-px w-12 bg-gradient-to-r from-transparent to-primary-400"></span>
              <span className="text-primary-600 text-sm uppercase tracking-widest font-medium flex items-center gap-2">
                <FaMagic className="text-secondary-500" />
                Building the Future, One Project at a Time
              </span>
              <span className="h-px w-12 bg-gradient-to-l from-transparent to-primary-400"></span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-primary-500 via-secondary-500 to-primary-500 bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
                We Create Digital
              </span>
              <br />
              <span className="text-neutral-800">Experiences That Matter</span>
            </h1>

            <p className="text-xl text-neutral-600 max-w-3xl mx-auto leading-relaxed">
              We're a passionate team of innovators, designers, and developers dedicated to building
              products that simplify life, inspire adventure, and empower people to achieve more.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 mb-20 max-w-2xl mx-auto">
            {[
              { number: '2', label: 'Live Products' },
              { number: '100%', label: 'Passion Driven' },
              { number: '24/7', label: 'Building & Improving' }
            ].map((stat, index) => (
              <div key={index} className="text-center p-6 bg-white rounded-2xl shadow-card hover:shadow-card-hover transition-shadow">
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary-500 to-secondary-500 bg-clip-text text-transparent mb-2">
                  {stat.number}
                </div>
                <div className="text-neutral-600 text-sm font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Our Story Section */}
      <div className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-neutral-600 leading-relaxed">
                <p>
                  It all started with a simple question: <em className="text-primary-500 font-medium">"How can we make everyday tasks easier and more enjoyable?"</em>
                </p>
                <p>
                  From that spark of curiosity, we embarked on a journey to create digital solutions
                  that genuinely improve people's lives. Whether it's helping you plan the perfect
                  vacation or guiding you through a home improvement project, we believe technology
                  should feel like a helpful friend, not a complicated tool.
                </p>
                <p>
                  Today, we're proud to serve thousands of users who trust our platforms to help
                  them explore the world and transform their living spaces. And we're just getting started.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-3xl transform rotate-3 opacity-20"></div>
              <div className="relative bg-gradient-to-br from-primary-50 to-secondary-50 rounded-3xl p-8 border border-neutral-100">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center shadow-lg">
                    <FaRocket className="text-3xl text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-neutral-800">Our Mission</h3>
                    <p className="text-neutral-500">What drives us every day</p>
                  </div>
                </div>
                <p className="text-neutral-700 italic text-lg leading-relaxed">
                  "To empower individuals with intelligent, beautifully designed tools that transform
                  complex tasks into delightful experiences."
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Our Projects Section */}
      <div className="py-20 bg-gradient-to-b from-neutral-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-4">
              Our Projects
            </h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              Each project is crafted with care, powered by innovation, and designed to make a real difference.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {projects.map((project, index) => {
              const Icon = project.icon;
              return (
                <a
                  key={index}
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group relative bg-white rounded-3xl p-8 border border-neutral-100 hover:border-transparent transition-all duration-500 ${project.shadowColor} hover:shadow-2xl hover:-translate-y-2`}
                >
                  {/* Gradient Overlay on Hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${project.color} opacity-0 group-hover:opacity-5 rounded-3xl transition-opacity duration-500`}></div>

                  <div className="relative">
                    <div className="flex items-start justify-between mb-6">
                      <div className={`w-16 h-16 bg-gradient-to-r ${project.color} rounded-2xl flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className="text-3xl text-white" />
                      </div>
                      <FaArrowRight className="text-neutral-300 group-hover:text-primary-500 transform group-hover:translate-x-2 transition-all duration-300" />
                    </div>

                    <h3 className="text-2xl font-bold text-neutral-800 mb-3 group-hover:text-primary-600 transition-colors">
                      {project.name}
                    </h3>

                    <p className="text-neutral-600 mb-6 leading-relaxed">
                      {project.description}
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {project.features.map((feature, idx) => (
                        <span
                          key={idx}
                          className={`px-3 py-1 bg-gradient-to-r ${project.color} bg-opacity-10 text-neutral-700 rounded-full text-sm font-medium`}
                          style={{ background: `linear-gradient(135deg, rgba(255,107,107,0.1), rgba(78,205,196,0.1))` }}
                        >
                          {feature}
                        </span>
                      ))}
                    </div>

                    <div className="mt-6 pt-6 border-t border-neutral-100">
                      <span className="text-sm text-neutral-500 group-hover:text-primary-500 transition-colors">
                        Visit {project.url.replace('https://', '')} &rarr;
                      </span>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </div>

      {/* Our Values Section */}
      <div className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-4">
              What We Stand For
            </h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              Our values aren't just words on a page - they're the principles that guide every decision we make.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="group p-6 bg-gradient-to-br from-neutral-50 to-white rounded-2xl border border-neutral-100 shadow-card hover:border-primary-200 hover:shadow-card-hover transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center mb-4 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                    <Icon className="text-2xl text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-neutral-800 mb-2">{value.title}</h3>
                  <p className="text-neutral-600 text-sm leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-500 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-20 h-20 border-2 border-white/20 rounded-full"></div>
          <div className="absolute bottom-10 right-10 w-32 h-32 border-2 border-white/20 rounded-full"></div>
          <div className="absolute top-1/2 left-1/4 w-4 h-4 bg-white/30 rounded-full"></div>
          <div className="absolute top-1/3 right-1/4 w-6 h-6 bg-white/20 rounded-full"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Whether you're planning your next adventure,
            we're here to help you every step of the way.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={BASE_URL}
              className="inline-flex items-center justify-center gap-2 bg-white text-primary-600 px-8 py-4 rounded-xl font-semibold hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              <FaPlane />
              Plan Your Trip
            </a>
          </div>
        </div>
      </div>

      {/* Internal Links for SEO */}
      <div className="py-12 bg-neutral-800">
        <div className="max-w-4xl mx-auto px-4">
          <InternalLinks currentPage="about" onNavigate={onNavigate} variant="compact" />
        </div>
      </div>

      {/* Footer Note */}
      <div className="py-12 bg-neutral-900 text-center">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-neutral-400 text-sm">
            Built with <FaHeart className="inline text-primary-500 mx-1" /> by a team that believes in making technology accessible,
            enjoyable, and genuinely useful for everyone.
          </p>
          <p className="text-neutral-500 text-xs mt-4">
            &copy; {new Date().getFullYear()} All rights reserved.
          </p>
        </div>
      </div>
      </div>
    </>
  );
};

export default AboutUs;
